<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Form</template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full px-4 sm:px-6 lg:px-6 bg-gray-200 border-r border-gray-400 pb-6 flex flex-col" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true" class="flex flex-col flex-grow">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="sm:col-span-12"
                                label="Form Name"
                                placeholder="Form Name"
                                name="name"
                                v-model="item.name"
                                rules="required"
                                :required="true" />
                        </div>
                        <div class="mt-4 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12 flex flex-col flex-grow" style="grid-template-rows: auto 1fr;">
                            <h2 class="w-full col-span-12 pb-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 h-8">Form Fields</h2>
                            <div v-if="item.schema.elements.length === 0" id="element_container" ref="element_container" class="border-dashed border-3 border-gray-400 flex flex-row justify-center items-center py-6 w-full col-span-12 flex-grow"
                                @drop="drop"
                                @dragover="dragover"
                                @dragenter="dragenter"
                                @dragleave="dragleave"
                            >
                                <span class="text-gray-500">Drop Form Fields Here</span>
                            </div>
                            <div v-else class="col-span-12 pb-32" id="element_list" ref="element_list"
                                @drop="drop"
                                @dragover="dragover"
                                @dragenter="dragenter"
                                @dragleave="dragleave">
                                <div class="grid grid-cols-12 col-gap-4 row-gap-4">
                                    <div v-for="(element, index) in item.schema.elements" :key="index"
                                        class="flex flex-col border-2 border-dashed cursor-pointer relative z-50 element"
                                        :class="[ 'col-span-'+element.width, selectedElement && selectedElement.id === element.id ? 'border-gray-700' : 'border-gray-400' ]"
                                        @click="selectElement(element, index)"
                                        @dragenter="elementDragEnter(index, $event)"
                                        @dragleave="elementDragLeave(index, $event)"
                                        @drop="elementDrop(index, $event)"
                                        @drag="elementDrag(index, $event)"
                                        :ref="'element_'+index"
                                        draggable="true"
                                    >
                                        <div class="w-full flex flex-row justify-center items-center absolute" style="top: -18px;" v-show="dropIndex === index">
                                            <hr class="w-full border-b border-2 border-black absolute">
                                            <span class="bg-black text-white z-10 text-2xs px-1 py-px rounded-md">Insert Here</span>
                                        </div>
                                        <label class="block text-sm font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer pt-4 px-4"
                                            :class="[element.required ? 'required' : '']"
                                        >{{ element.label }}</label>
                                        <div class="px-4 pb-4">
                                            <template v-if="element.type === 'textInput'">
                                                <input type="text" value="" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                            </template>
                                            <template v-if="element.type === 'longTextInput'">
                                                <textarea rows="5" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" ></textarea>
                                            </template>
                                            <template v-if="element.type === 'numberInput'">
                                                <input type="number" value="" :placeholder="element.placeholder.visible ? element.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                            </template>
                                            <template v-if="element.type === 'selectInput'">
                                                <select class="border-gray-500 border py-2 px-2 block w-full transition duration-150 ease-in-out text-sm leading-none text-gray-700">
                                                    <option value="" v-if="element.placeholder.visible" disabled selected hidden>{{ element.placeholder.value }}</option>
                                                    <option v-for="(option, optionIndex) in element.options" :value="option.value" :key="optionIndex">{{ option.label }}</option>
                                                </select>
                                            </template>
                                            <template v-if="element.type === 'radioInput'">
                                                <div class="flex" :class="[ element.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                                    <label v-for="(option, optionIndex) in element.options" :key="optionIndex"
                                                        class="mr-4 flex flex-row items-center cursor-pointer"
                                                        :class="[ element.display === 'vertical' ? 'mb-2' : '' ]"
                                                    >
                                                        <input type="radio" :value="option.value" :name="element.id"><span class="ml-1 text-sm">{{ option.label }}</span>
                                                    </label>
                                                </div>
                                            </template>
                                            <template v-if="element.type === 'checkboxInput'">
                                                <div class="flex" :class="[ element.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                                    <label v-for="(option, optionIndex) in element.options" :key="optionIndex"
                                                        class="mr-4 flex flex-row items-center cursor-pointer"
                                                        :class="[ element.display === 'vertical' ? 'mb-2' : '' ]"
                                                    >
                                                        <input type="checkbox" :value="option.value" :name="element.id+'[]'"><span class="ml-1 text-sm">{{ option.label }}</span>
                                                    </label>
                                                </div>
                                            </template>
                                            <template v-if="element.type === 'fileUpload'">
                                                <button type="button"
                                                    class="float-left bg-default-500 text-white text-xs py-1 px-3"
                                                    :class="{'opacity-50 cursor-not-allowed': disabled}"
                                                >
                                                    <font-awesome-icon class="text-white text-sm mr-2" :icon="['fal', 'upload']" />
                                                    {{ element.button }}
                                                </button>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
                <div class="md:w-1/4 sm:w-full px-4">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-3 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12" ref="properties">
                            <div class="col-span-12 flex flex-col">
                                <ul class="list-none flex -mb-px z-10">
                                    <li class="mr-1" :class="{'-mb-px': sidebarTab === 'properties'}" v-if="selectedElement">
                                        <a href="javascript:void(0)"
                                            class="inline-block py-2 px-2 no-underline text-gray-800 px-6 text-sm border-t-2 border-l border-r"
                                            :class="{'bg-tab-content': sidebarTab === 'properties', 'header-bg border-transparent hover:bg-gray-100': sidebarTab !== 'properties'}"
                                            @click="sidebarTab = 'properties'">
                                            Properties
                                        </a>
                                    </li>
                                    <li class="mr-1 flex-grow" :class="{'-mb-px': sidebarTab === 'elements'}">
                                        <a href="javascript:void(0)"
                                            class="inline-block py-2 px-2 no-underline text-gray-800 px-6 text-sm border-t-2 border-l border-r"
                                            :class="{'bg-tab-content': sidebarTab === 'elements', 'header-bg border-transparent hover:bg-gray-100': sidebarTab !== 'elements'}"
                                            @click="sidebarTab = 'elements'">
                                            Elements
                                        </a>
                                    </li>
                                </ul>
                                <div class="border border-gray-400 grid grid-cols-2 p-2 col-gap-2 row-gap-2 bg-gray-200" v-if="sidebarTab === 'elements'">
                                    <button v-for="(element, index) in elements" :key="index"
                                        type="button"
                                        class="bg-charcoal-100 text-charcoal-500 text-xs py-2"
                                        draggable="true"
                                        @dragstart="dragstart(element, $event)"
                                        @dragend="dragend($event)"
                                        @click="preventDefault($event)"
                                    >
                                        {{ element.name }}
                                    </button>
                                </div>
                                <div class="border border-gray-400 grid grid-cols-12 p-2 col-gap-2 row-gap-2 bg-gray-200" v-if="sidebarTab === 'properties'">
                                    <div class="col-span-12 flex flex-col">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Label
                                        </label>
                                        <input type="text" v-model="selectedElement.label" :placeholder="'Label'" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </div>
                                    <div class="col-span-12 flex flex-col" v-if="selectedElement.type === 'fileUpload'">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Button Label
                                        </label>
                                        <input type="text" v-model="selectedElement.button" :placeholder="'Upload'" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </div>
                                    <div class="col-span-12 flex flex-col" v-if="selectedElement.display">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Display
                                        </label>
                                        <select v-model="selectedElement.display" class="border-gray-500 border py-2 px-2 block w-full transition duration-150 ease-in-out text-sm leading-none text-gray-700">
                                            <option value="horizontal">Horizontal</option>
                                            <option value="vertical">Vertical</option>
                                        </select>
                                    </div>
                                    <div class="col-span-12 flex flex-col" v-if="selectedElement.placeholder">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Placeholder
                                            <toggle-button v-if="selectedElement.placeholder.visible" class="ml-1 -mt-px float-right"
                                                v-model="selectedElement.placeholder.visible"
                                                :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                                                :sync="true"
                                                :height="15"
                                                :width="30"/>
                                        </label>
                                        <toggle-button v-if="!selectedElement.placeholder.visible"
                                            v-model="selectedElement.placeholder.visible"
                                            :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                                            :sync="true"
                                            :height="39"
                                            :width="80"/>
                                        <input type="text" v-if="selectedElement.placeholder.visible" v-model="selectedElement.placeholder.value" :placeholder="'Placeholder'" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </div>
                                    <div class="col-span-12 flex flex-col">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Help Block
                                            <toggle-button v-if="selectedElement.help.visible" class="ml-1 -mt-px float-right"
                                                v-model="selectedElement.help.visible"
                                                :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                                                :sync="true"
                                                :height="15"
                                                :width="30"/>
                                        </label>
                                        <toggle-button v-if="!selectedElement.help.visible"
                                            v-model="selectedElement.help.visible"
                                            :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                                            :sync="true"
                                            :height="39"
                                            :width="80"/>
                                        <input type="text" v-if="selectedElement.help.visible" v-model="selectedElement.help.value" :placeholder="'Help Details'" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </div>
                                    <div class="col-span-4 flex flex-col">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Required
                                        </label>
                                        <toggle-button v-model="selectedElement.required"
                                            :color="{checked: '#38a169', unchecked: '#CCCCCC'}"
                                            :sync="true"
                                            :height="40"
                                            :width="80"/>
                                    </div>
                                    <div class="col-span-4 flex flex-col">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Width
                                        </label>
                                        <select v-model="selectedElement.width" class="border-gray-500 border py-2 px-2 block w-full transition duration-150 ease-in-out text-sm leading-none text-gray-700">
                                            <option value="3">25%</option>
                                            <option value="4">33%</option>
                                            <option value="6">50%</option>
                                            <option value="9">75%</option>
                                            <option value="12">100%</option>
                                        </select>
                                    </div>
                                    <div class="col-span-12 flex flex-col" v-if="selectedElement.options">
                                        <label class="block font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer text-xs">
                                            Options
                                        </label>
                                        <div class="flex flex-row mb-2" v-for="(option, optionIndex) in selectedElement.options" :key="option.id">
                                            <input type="text" placeholder="Name" v-model="option.label" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700 mr-2"/>
                                            <input type="text" placeholder="Value (Optional)" v-model="option.value" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700"/>
                                            <button type="button" class="pr-1" @click="removeOption(optionIndex)">
                                                <font-awesome-icon class="text-lg outline-none text-default-500 ml-3" :icon="['fas', 'times']" />
                                            </button>
                                        </div>
                                        <button type="button" class="bg-default-500 hover:bg-default-600 text-white px-4 py-1 text-xs border-0 w-1/3" @click="addOption">
                                            <font-awesome-icon class="text-white text-xs mr-2" :icon="['fal', 'plus']"/>
                                            Add Option
                                        </button>
                                    </div>
                                    <div class="col-span-12 flex flex-row w-full items-end justify-end pt-2" v-if="selectedElement.options">
                                        <font-awesome-icon class="text-lg outline-none text-default-500 float-right cursor-pointer" :icon="['fas', 'trash-alt']" @click="deleteElement" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
    import FormSetup from "@/mixins/Tabs";
    import FormButtons from "@/mixins/FormButtons";
    import axios from "axios";

    export default {
        name: "FormForm",
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/configuration/application-forms'},
            page: {required: false, default: 'forms'}
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'forms',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ]
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    }
                ],
                startTab: null,
                sidebarTab: 'elements',
                loading: true,
                submitted: false,
                errorMessage: '',
                item: {
                    schema: {
                        elements: [],
                        fields: 0
                    }
                },
                S3URL: process.env.VUE_APP_S3_URL,
                elements: [
                    {
                        name: 'Text',
                        type: 'textInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        placeholder: {
                            visible: true,
                            value: 'Placeholder',
                        },
                        width: '12'
                    },
                    {
                        name: 'Long Text',
                        type: 'longTextInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        placeholder: {
                            visible: true,
                            value: 'Placeholder',
                        },
                        width: '12'
                    },
                    {
                        name: 'Number',
                        type: 'numberInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        placeholder: {
                            visible: true,
                            value: 'Placeholder',
                        },
                        width: '12'
                    },
                    {
                        name: 'Select',
                        type: 'selectInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        placeholder: {
                            visible: true,
                            value: 'Placeholder',
                        },
                        options: [
                            {
                                id: '',
                                label: 'Option 1',
                                value: 'Option 1'
                            },
                            {
                                id: '',
                                label: 'Option 2',
                                value: 'Option 2'
                            }
                        ],
                        width: '12'
                    },
                    {
                        name: 'Radio',
                        type: 'radioInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        options: [
                            {
                                id: '',
                                label: 'Option 1',
                                value: 'Option 1'
                            },
                            {
                                id: '',
                                label: 'Option 2',
                                value: 'Option 2'
                            }
                        ],
                        width: '12',
                        display: 'horizontal'
                    },
                    {
                        name: 'Checkbox',
                        type: 'checkboxInput',
                        label: 'Label',
                        required: false,
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        options: [
                            {
                                id: '',
                                label: 'Option 1',
                                value: 'Option 1'
                            },
                            {
                                id: '',
                                label: 'Option 2',
                                value: 'Option 2'
                            }
                        ],
                        width: '12',
                        display: 'horizontal'
                    },
                    {
                        name: 'File Upload',
                        type: 'fileUpload',
                        label: 'Label',
                        required: false,
                        button: 'Upload',
                        help: {
                            visible: false,
                            value: 'Help Content Here'
                        },
                        width: '12'
                    },
                ],
                draggingItem: null,
                selectedElement: null,
                selectedElementIndex: null,
                dropIndex: null,
                dragIndex: null,
                sidebarWidth: null
            }
        },
        methods: {
            deleteElement: function(){
                this.item.schema.elements.splice(this.selectedElementIndex, 1);
                this.$nextTick(() => {
                    this.selectedElement = null;
                    this.selectedElementIndex = null;
                    this.sidebarTab = 'elements';
                });
            },
            addOption: function(){
                this.selectedElement.options.push({
                    label: '',
                    value: ''
                });
            },
            removeOption: function(index){
                this.selectedElement.options.splice(index, 1);
            },
            selectElement: function(element, index){
                this.selectedElement = element;
                this.selectedElementIndex = index;
                this.sidebarTab = 'properties';
            },
            dragstart: function(element, e){
                this.draggingItem = element;
                e.target.style.opacity = 0.5;
            },
            dragend: function(e) {
                this.draggingItem = null;
                e.target.style.opacity = 1;
            },
            dragenter: function(e){
                const parentChildren = Array.from(e.target.parentElement.children);

                if (e.target.style && (e.target.id === 'element_list' || e.target.id === 'element_container') && this.dragIndex === null){
                    e.target.style['border-style'] = "dashed";
                    e.target.style.background = "#efefef";
                    this.dropIndex = null;
                }
            },
            dragover: function(e){
                e.preventDefault();
            },
            dragleave: function(e){
                if (e.target.style){
                    e.target.style['border-style'] = "";
                    e.target.style.background = "";
                }
            },
            drop: function(e){
                e.preventDefault();

                e.target.style['border-style'] = "";
                e.target.style.background = "";

                if (e.target.id === 'element_list' || e.target.id === 'element_container') {
                    const copyOfItem = JSON.parse(JSON.stringify(this.draggingItem));
                    copyOfItem.id = 'field_' + this.item.schema.fields;

                    if (copyOfItem.options){
                        copyOfItem.options.forEach(option => {
                            option.id = this.makeID(6);
                        });
                    }

                    this.item.schema.fields++;
                    this.item.schema.elements.push(copyOfItem);

                    this.selectedElement = this.item.schema.elements[this.item.schema.elements.length - 1];
                    this.selectedElementIndex = this.item.schema.elements.length - 1;
                    this.sidebarTab = 'properties';
                } else {
                    this.dropIndex = null;
                    this.dragIndex = null;
                }
            },
            elementDragEnter: function(index, e){
                this.dropIndex = index;
            },
            elementDragLeave: function(index, e){

            },
            elementDrag: function(index, e){
                this.dragIndex = index;
            },
            elementDrop: function(index, e){
                e.preventDefault();

                if (this.dragIndex >= 0){
                    if (this.draggingItem){
                        const copyOfItem = JSON.parse(JSON.stringify(this.draggingItem));
                        copyOfItem.id = 'field_' + this.item.schema.fields;

                        if (copyOfItem.options){
                            copyOfItem.options.forEach(option => {
                                option.id = this.makeID(6);
                            });
                        }

                        this.item.schema.fields++;
                        this.item.schema.elements.splice(this.dropIndex, 0, copyOfItem);

                        this.selectedElement = this.item.schema.elements[this.item.schema.elements.length - 1];
                        this.selectedElementIndex = this.item.schema.elements.length - 1;
                        this.sidebarTab = 'properties';
                        this.dropIndex = null;
                        this.dragIndex = null;
                        this.draggingItem = null;
                    } else {
                        const f = this.item.schema.elements.splice(this.dragIndex, 1)[0];
                        this.item.schema.elements.splice(this.dropIndex, 0, f);
                    }
                } else {
                    const copyOfItem = JSON.parse(JSON.stringify(this.draggingItem));
                    copyOfItem.id = 'field_'+this.item.schema.fields;

                    if (copyOfItem.options){
                        copyOfItem.options.forEach(option => {
                            option.id = this.makeID(6);
                        });
                    }

                    this.item.schema.fields++;
                    this.item.schema.elements.splice(this.dropIndex, 0, copyOfItem);

                    this.selectedElement = this.item.schema.elements[this.dropIndex];
                    this.sidebarTab = 'properties';
                }

                this.dropIndex = null;
                this.dragIndex = null;
            },
            preventDefault: function(e){
                e.preventDefault();
            },
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/forms/' + this.id, )
                    .then(response => {
                        this.item = response.data;
                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            submitForm: function () {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;

                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/forms/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/forms/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/forms';
                        _method = 'POST';
                    }

                    axios({
                        method: _method,
                        url: _url,
                        data: this.item
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});

                            if (this.return) {
                                this.$router.push(this.return);
                            } else {
                                this.$router.push('/configuration/application-forms');
                            }

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            makeID: function(length) {
                let result = '';
                const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                const charactersLength = characters.length;
                for (let i = 0; i < length; i++ ) {
                    result += characters.charAt(Math.floor(Math.random() * charactersLength));
                }
                return result;
            },
            handleScroll: function(e){
                if (this.$refs['properties']) {
                    if (!this.sidebarWidth) {
                        this.sidebarWidth = this.$refs['properties'].offsetWidth;
                        console.log(this.sidebarWidth);
                    }

                    if (e.target.scrollTop > 110) {
                        this.$refs['properties'].style.position = 'fixed';
                        this.$refs['properties'].style.top = '66px';
                        this.$refs['properties'].style.width = this.sidebarWidth + 'px';
                    } else {
                        this.$refs['properties'].style.position = 'relative';
                        this.$refs['properties'].style.top = 'initial';
                    }
                }
            }
        },
        mounted() {
            document.querySelector('#main_container').addEventListener('scroll', (e) => {
                this.handleScroll(e);
            });

            if (this.type === "New") {
                this.loading = false;
                this.$Progress.finish();
                // Se defaults here
            } else if (this.type === "Edit") {
                this.getData();
            }

            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        }
    }
</script>

<style scoped>

</style>
